import type {ReactElement, ReactNode} from 'react';
import React from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {SlideDown} from 'react-slidedown';
import {ClickableDiv} from '@Components/clickable-div';
import type {AccordionHeaderOpts, AccordionIconOpts} from './controlled-accordion.types';
import {AccordionHeaderSpacing} from './controlled-accordion.types';
import 'react-slidedown/lib/slidedown.css';
import styles from './controlled-accordion.module.scss';
import './controlled-accordion.scss';

interface SlideDownWrapperProps {
  isOpen: boolean;
  doHideOnClose: boolean;
  children: ReactNode;
}

const SlideDownWrapper = React.memo(
  function SlideDownWrapper({isOpen, doHideOnClose, children}: SlideDownWrapperProps): ReactElement {
    if (doHideOnClose) {
      return (
        <SlideDown>
          <div className={`${styles.hidingAccordion} ${isOpen ? styles.show : styles.hide}`}>{children}</div>
        </SlideDown>
      );
    }
    return <SlideDown>{isOpen ? children : null}</SlideDown>;
  },
  (prev, next) => prev.isOpen === next.isOpen && prev.doHideOnClose === next.doHideOnClose
);

interface AccordionPropsGeneral {
  header: ReactNode;
  children: ReactNode;
  isOpen: boolean;
  onHeaderClick: (isOpen: boolean) => void;
  className?: string;
  'data-testid'?: string;
  /**
   * Classes applies to the accordion container when the accordion is open
   */
  openClasses?: string;
  doHideOnClose?: boolean;
}

export type ControlledAccordionProps = AccordionPropsGeneral & AccordionHeaderOpts & AccordionIconOpts;

export function ControlledAccordion({
  header,
  children,
  isOpen,
  onHeaderClick,
  className = '',
  iconClassName = '',
  iconSize = IconSize.SIZE_ICON_24,
  iconType = IconType.GHOST,
  headerPadding = AccordionHeaderSpacing.MEDIUM,
  headerClassName = '',
  hasUnstyledHeader = false,
  removeHeaderHoverStateWhenOpen = false,
  removeHeaderHoverStateAlways = false,
  openClasses = '',
  doHideOnClose = false,
  'data-testid': dataTestId,
}: ControlledAccordionProps): ReactElement<any> {
  const getMainHeaderClasses = (): string => {
    return `
      ${styles.header} 
      ${isOpen ? styles.open : ''} 
      ${headerPadding} 
      ${removeHeaderHoverStateAlways || (removeHeaderHoverStateWhenOpen && isOpen) ? styles.noHover : ''}
      ${hasUnstyledHeader ? styles.unstyledHeader : 'radius-4'}
      flex-row-align-center 
      flex-justify-between
      ${headerClassName} 
    `;
  };

  const onClick = (): void => {
    onHeaderClick(!isOpen);
  };

  return (
    <div className={`accordion-container ${className} ${isOpen ? openClasses : ''}`}>
      <ClickableDiv data-testid={dataTestId} onClick={onClick} className={getMainHeaderClasses()}>
        {header}
        <Icon icon={isOpen ? 'icon-caret-up' : 'icon-caret-down'} className={`${iconClassName} spacing-m-l-2`} size={iconSize} type={iconType} shape={IconShape.NONE} />
      </ClickableDiv>
      <SlideDownWrapper isOpen={isOpen} doHideOnClose={doHideOnClose}>
        {children}
      </SlideDownWrapper>
    </div>
  );
}
